import type { PolicyConfig } from '../components/PolicyConfigTabs';

export type RiskLevel = 'critical' | 'warning' | 'good';

export interface GatewayAssessment {
	direct_channels: Record<string, { enabled: boolean }>;
	gateway_mode: string;
	agent_count: number;
}

export interface SecurityRiskResult {
	// Managed channels — integrations routed through ClawdGate threat detection
	managedChannels: string[];

	threatDetectionEnabled: boolean;
	threatHasProtectiveActions: boolean;
	threatRisk: RiskLevel;

	pamEnabled: boolean;
	pamHighRiskProtected: boolean;
	pamRisk: RiskLevel;

	// Direct/unmanaged channels from gateway assessment
	directChannels: string[];
	directChannelRisk: RiskLevel;
	assessedAt: number | null;

	overallRisk: RiskLevel;
}

export interface AggregateSecurityRisk {
	totalInstances: number;
	instancesWithManagedChannels: number;
	managedChannelNames: string[];
	instancesWithThreatDetectionOff: number;
	threatDetectionOffWorkspaces: string[];
	instancesWithWeakThreatActions: number;
	weakThreatActionsWorkspaces: string[];
	instancesWithPamOff: number;
	pamOffWorkspaces: string[];
	instancesWithWeakPam: number;
	weakPamWorkspaces: string[];
	instancesWithDirectChannels: number;
	directChannelNames: string[];
	directChannelWorkspaces: string[];
	overallRisk: RiskLevel;
}

const CHANNEL_KEYS = ['telegram', 'slack', 'discord', 'webhook', 'local_port', 'email', 'wechat', 'whatsapp', 'imessage'] as const;

function worstRisk(...levels: RiskLevel[]): RiskLevel {
	if (levels.includes('critical')) return 'critical';
	if (levels.includes('warning')) return 'warning';
	return 'good';
}

export function computeSecurityRisk(
	config: PolicyConfig,
	gatewayAssessment?: GatewayAssessment | null,
	assessedAt?: number | null,
): SecurityRiskResult {
	// --- Managed channels (integrations routed through ClawdGate) ---
	const managedChannels: string[] = [];
	if (config.integrations) {
		for (const key of CHANNEL_KEYS) {
			const integration = config.integrations[key];
			if (integration?.enabled) {
				managedChannels.push(key);
			}
		}
	}

	// --- Threat detection risk ---
	const threatDetectionEnabled = !!config.enable_threat_detection;
	let threatHasProtectiveActions = false;
	if (threatDetectionEnabled && config.threat_actions) {
		const actions = config.threat_actions;
		const criticalAction = actions.critical;
		const highAction = actions.high;
		threatHasProtectiveActions =
			criticalAction === 'block' || criticalAction === 'request' ||
			highAction === 'block' || highAction === 'request';
	}
	let threatRisk: RiskLevel;
	if (!threatDetectionEnabled) {
		threatRisk = 'critical';
	} else if (!threatHasProtectiveActions) {
		threatRisk = 'warning';
	} else {
		threatRisk = 'good';
	}

	// --- PAM risk ---
	const pamEnabled = !!config.enable_pam;
	let pamHighRiskProtected = false;
	if (pamEnabled && config.pam_scopes) {
		const scopes = config.pam_scopes;
		const destructiveAction = scopes.destructive?.action;
		const credentialAction = scopes.credential?.action;
		pamHighRiskProtected =
			(destructiveAction === 'block' || destructiveAction === 'mfa_challenge') &&
			(credentialAction === 'block' || credentialAction === 'mfa_challenge');
	}
	let pamRisk: RiskLevel;
	if (!pamEnabled) {
		pamRisk = 'critical';
	} else if (!pamHighRiskProtected) {
		pamRisk = 'warning';
	} else {
		pamRisk = 'good';
	}

	// --- Direct channel risk (from gateway assessment) ---
	const directChannels: string[] = [];
	let directChannelRisk: RiskLevel = 'good';
	if (gatewayAssessment?.direct_channels) {
		const managedSet = new Set(managedChannels);
		for (const [name, status] of Object.entries(gatewayAssessment.direct_channels)) {
			if (status.enabled && !managedSet.has(name)) {
				directChannels.push(name);
			}
		}
		if (directChannels.length > 0) {
			directChannelRisk = 'critical';
		}
	}

	return {
		managedChannels,
		threatDetectionEnabled,
		threatHasProtectiveActions,
		threatRisk,
		pamEnabled,
		pamHighRiskProtected,
		pamRisk,
		directChannels,
		directChannelRisk,
		assessedAt: assessedAt ?? null,
		overallRisk: worstRisk(threatRisk, pamRisk, directChannelRisk),
	};
}

export function aggregateSecurityRisk(
	instances: { instance_name?: string | null; hostname?: string | null; config: PolicyConfig; gateway_assessment?: GatewayAssessment | null; gateway_assessed_at?: number | null }[],
): AggregateSecurityRisk {
	let instancesWithManagedChannels = 0;
	const allChannelNames = new Set<string>();
	let instancesWithThreatDetectionOff = 0;
	const threatDetectionOffWorkspaces: string[] = [];
	let instancesWithWeakThreatActions = 0;
	const weakThreatActionsWorkspaces: string[] = [];
	let instancesWithPamOff = 0;
	const pamOffWorkspaces: string[] = [];
	let instancesWithWeakPam = 0;
	const weakPamWorkspaces: string[] = [];
	let instancesWithDirectChannels = 0;
	const allDirectChannelNames = new Set<string>();
	const directChannelWorkspaces: string[] = [];

	for (const inst of instances) {
		const risk = computeSecurityRisk(inst.config, inst.gateway_assessment, inst.gateway_assessed_at);
		const workspaceName = inst.instance_name || inst.hostname || 'Unknown';

		if (risk.managedChannels.length > 0) {
			instancesWithManagedChannels++;
			for (const ch of risk.managedChannels) allChannelNames.add(ch);
		}

		if (!risk.threatDetectionEnabled) {
			instancesWithThreatDetectionOff++;
			threatDetectionOffWorkspaces.push(workspaceName);
		} else if (!risk.threatHasProtectiveActions) {
			instancesWithWeakThreatActions++;
			weakThreatActionsWorkspaces.push(workspaceName);
		}

		if (!risk.pamEnabled) {
			instancesWithPamOff++;
			pamOffWorkspaces.push(workspaceName);
		} else if (!risk.pamHighRiskProtected) {
			instancesWithWeakPam++;
			weakPamWorkspaces.push(workspaceName);
		}

		if (risk.directChannels.length > 0) {
			instancesWithDirectChannels++;
			directChannelWorkspaces.push(workspaceName);
			for (const ch of risk.directChannels) allDirectChannelNames.add(ch);
		}
	}

	const hasAnyCritical =
		instancesWithThreatDetectionOff > 0 ||
		instancesWithPamOff > 0 ||
		instancesWithDirectChannels > 0;

	const hasAnyWarning =
		instancesWithWeakThreatActions > 0 ||
		instancesWithWeakPam > 0;

	const overallRisk: RiskLevel = hasAnyCritical ? 'critical' : hasAnyWarning ? 'warning' : 'good';

	return {
		totalInstances: instances.length,
		instancesWithManagedChannels,
		managedChannelNames: [...allChannelNames],
		instancesWithThreatDetectionOff,
		threatDetectionOffWorkspaces,
		instancesWithWeakThreatActions,
		weakThreatActionsWorkspaces,
		instancesWithPamOff,
		pamOffWorkspaces,
		instancesWithWeakPam,
		weakPamWorkspaces,
		instancesWithDirectChannels,
		directChannelNames: [...allDirectChannelNames],
		directChannelWorkspaces,
		overallRisk,
	};
}
